import axios from 'axios';
import { AuditLog } from '../interfaces/UploadedFileDataInterface';
import { TranscriptSelectionSummary, TranscriptDetails, TranscripsCategoriesKeys, FinalizedTranscriptDetailsResponse, CategorizedSelectedTranscriptsIds } from '../interfaces/TranscriptsInterface';
import { TRANSCRIPTS_CATEGORIES } from '../constants';
const BASE_URL = process.env.REACT_APP_BASE_URL;

const getTranscriptSelectionSummary = async (eoaId: string, access_token: string, school_id: string, target_bucket: TranscripsCategoriesKeys, page?: number, next_batch?: boolean)=> {
  if (!school_id) return {
    data: {
      has_next_batch: false,
      is_finalized: false,
      selection_counts: {
        [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 0,
        [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 0,
        [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 0,
      },
      transcripts: {
        [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: [],
        [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: [],
        [TRANSCRIPTS_CATEGORIES.STRUGGLING]: []
      }
    },
    headers: {
      link: '',
      'x-total-count': ''
    },
  };

  return await axios.get<TranscriptSelectionSummary>(`${BASE_URL}/eoa/${eoaId}/schools/${school_id}/transcripts/transcript-selection-summary/${target_bucket}`, {
    params: {page: page ? page : 0, next_batch: next_batch ? next_batch : false},
    headers: {
      Authorization: `Bearer ${access_token}`
    },
  });
};

const getTranscriptsFromAllBuckets = async (eoaId: string, access_token: string, school_id: string, page?: number, is_selected?: boolean)=> {
  if (!school_id) return {
    data: {
      is_finalized: false,
      selection_counts: {
        [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 0,
        [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 0,
        [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 0,
      },
      transcripts: {
        [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: [],
        [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: [],
        [TRANSCRIPTS_CATEGORIES.STRUGGLING]: []
      }
    },
    headers: {
      link: ''
    },
  };

  return await axios.get<TranscriptSelectionSummary>(`${BASE_URL}/eoa/${eoaId}/schools/${school_id}/transcripts/transcript-selection-summary`, {
    params: {page: page ? page : 0, is_selected: is_selected ? is_selected : false},
    headers: {
      Authorization: `Bearer ${access_token}`
    },
  });
};

const getTranscriptDetails = async (eoaId: string, access_token: string, school_id: string, transcript_id: string) => {

  return await axios.get<TranscriptDetails>(`${BASE_URL}/eoa/${eoaId}/schools/${school_id}/transcripts/${transcript_id}`, {
    headers: {
      Authorization: `Bearer ${access_token}`
    },
  });
};

const getTranscriptsDetails = async (eoaId: string, access_token: string, school_id: string, transcripts_ids: string[]) => {
  let paths = transcripts_ids.map(transcriptId => `${BASE_URL}/eoa/${eoaId}/schools/${school_id}/transcripts/${transcriptId}`);

  const requests = paths.map(path => 
    axios.get<TranscriptDetails>(path, {
      headers: {
        Authorization: `Bearer ${access_token}`
      }
    })
  );

  return await Promise.allSettled(requests);
};

const getFinalizedTranscriptsDetails = async (eoaId: string, access_token: string, school_id: string) => {
  return await axios.get<FinalizedTranscriptDetailsResponse>(`${BASE_URL}/eoa/${eoaId}/schools/${school_id}/transcripts/selected`, {
    headers: {
      Authorization: `Bearer ${access_token}`
    }
  });
};

const getAllSelectedTranscripts = async (eoaId: string, access_token: string, schoolIds: string[]) => {
  let paths = schoolIds.map(school_id => `${BASE_URL}/eoa/${eoaId}/schools/${school_id}/transcripts/selected`);

  const requests = paths.map(path => 
    axios.get<FinalizedTranscriptDetailsResponse>(path, {
      headers: {
        Authorization: `Bearer ${access_token}`
      }
    })
  );

  return await Promise.allSettled(requests);
};

const saveSingleSelection = async (eoaId: string, access_token: string, school_id: string, transcript: {category: string, transcript_id: string}) => {

  return await axios.post<TranscriptDetails>(`${BASE_URL}/eoa/${eoaId}/schools/${school_id}/transcripts/save-single-selection`,
    transcript,
    {
      headers: {
        Authorization: `Bearer ${access_token}`
      },
    });
};

const unsaveSingleSelection = async (eoaId: string, access_token: string, school_id: string, transcript: {category: string, transcript_id: string}) => {

  return await axios.post<TranscriptDetails>(`${BASE_URL}/eoa/${eoaId}/schools/${school_id}/transcripts/unsave-single-selection`, 
    transcript,
    {
      headers: {
        Authorization: `Bearer ${access_token}`
      },
    });
};

const finalizeTranscriptSelection = async (eoaId: string, access_token: string, school_id: string, categorizedData: CategorizedSelectedTranscriptsIds) => {
  return await axios.post(`${BASE_URL}/eoa/${eoaId}/schools/${school_id}/transcripts/finalize-selection`,
    categorizedData,
    {
      headers: {
        Authorization: `Bearer ${access_token}`
      },
    }
  );
};

const getAuditLog = async (eoaId: string, access_token: string, page?: number, size?: number) => {

  const params = {page: page, size: size};

  return await axios.get<AuditLog>(`${BASE_URL}/eoa/${eoaId}/audit-log/data-file/`,
    {
      headers: {
        Authorization: `Bearer ${access_token}`
      },
      params
    }
  );
};

const getAllTrascriptSelectionSummaries = async (eoaId: string, access_token: string, schoolIds: string[], target_bucket: TranscripsCategoriesKeys) => {
  let paths = schoolIds.map(schoolId => `${BASE_URL}/eoa/${eoaId}/schools/${schoolId}/transcripts/transcript-selection-summary/${target_bucket}`);

  const requests = paths.map(path => 
    axios.get<TranscriptSelectionSummary>(path, {
      headers: {
        Authorization: `Bearer ${access_token}`
      }
    })
  );

  return await Promise.allSettled(requests);
};

export {
  getTranscriptSelectionSummary,
  getTranscriptsFromAllBuckets,
  getTranscriptDetails,
  getTranscriptsDetails,
  getFinalizedTranscriptsDetails,
  getAllSelectedTranscripts,
  saveSingleSelection,
  unsaveSingleSelection,
  finalizeTranscriptSelection,
  getAuditLog,
  getAllTrascriptSelectionSummaries
};